import { Button, buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Plus, Sun, User } from "lucide-react";
import { CanAccess, useTranslate } from "ra-core";
import {
  Link,
  matchPath,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router";
import { ContactCreateSheet } from "../contacts/ContactCreateSheet";
import { useState } from "react";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";
import { useBitrixMode, useCrmModule } from "../misc/fromBitrix";
import { findActiveNavItem, visibleNavItems } from "./navigation";

// The center "+" sits after Today, Leads and Deals: three buttons on each side.
const CREATE_BUTTON_POSITION = 3;

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();
  const activeKey = matchPath("/", location.pathname)
    ? "today"
    : findActiveNavItem(location.pathname);
  const crmModule = useCrmModule();

  // Check if the app is running as a PWA (standalone mode)
  const isPwa = window.matchMedia("(display-mode: standalone)").matches;
  // Check if it's iOS on the web
  const isWebiOS = /iPad|iPod|iPhone/.test(window.navigator.userAgent);

  const buttons = [
    <NavigationButton
      key="today"
      href="/"
      Icon={Sun}
      label={translate("crm.navigation.today")}
      isActive={activeKey === "today"}
    />,
    ...visibleNavItems(crmModule)
      .filter((item) => !item.desktopOnly)
      .map((item) => {
        const button = (
          <NavigationButton
            key={item.key}
            href={item.to}
            Icon={item.icon}
            label={translate(item.label, { smart_count: 2 })}
            isActive={activeKey === item.key}
          />
        );
        return item.access ? (
          <CanAccess key={item.key} {...item.access}>
            {button}
          </CanAccess>
        ) : (
          button
        );
      }),
    <NavigationButton
      key="profile"
      href="/settings"
      Icon={User}
      label={translate("crm.profile.title")}
      isActive={!!matchPath("/settings/*", location.pathname)}
    />,
  ];
  // nothing to create without the CRM module
  if (crmModule) {
    buttons.splice(CREATE_BUTTON_POSITION, 0, <CreateButton key="create" />);
  }

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t h-14"
      style={{
        // iOS bug: even though viewport is set correctly, the bottom safe area inset is not accounted for
        // So we manually add some padding to avoid the navigation being too close to the home bar
        paddingBottom: isPwa && isWebiOS ? 15 : undefined,
        // We use box-sizing: border-box, so the height contains the padding.
        // To actually increase the padding, we need to increase the height as well
        height:
          "calc(var(--spacing)) * 6" + (isPwa && isWebiOS ? " + 15px" : ""),
      }}
    >
      <div className="flex justify-center px-1">{buttons}</div>
    </nav>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Link
    to={href}
    className={cn(
      buttonVariants({ variant: "ghost" }),
      // flexible width: up to seven buttons share a 390 px screen
      "flex-col gap-1 h-auto py-2 px-0.5 rounded-md flex-1 min-w-0 max-w-20",
      isActive ? "text-primary-ink" : "text-muted-foreground",
    )}
    aria-current={isActive ? "page" : undefined}
  >
    <Icon className="size-6" />
    <span className="text-[0.6rem] font-medium max-w-full truncate">
      {label}
    </span>
  </Link>
);

const CreateButton = () => {
  const translate = useTranslate();
  const contact_id = useMatch("/contacts/:id/*")?.params.id;
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);
  const navigate = useNavigate();
  // deals and contacts are made in Bitrix24 in Bitrix24 mode
  const bitrix = useBitrixMode();

  return (
    <>
      <ContactCreateSheet
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
      />
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={contact_id}
      />
      <TaskCreateSheet
        open={taskCreateOpen}
        onOpenChange={setTaskCreateOpen}
        contact_id={contact_id}
      />
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="default"
              size="icon"
              className="h-14 w-14 shrink-0 rounded-full -mt-3 mx-1"
              aria-label={translate("ra.action.create")}
            />
          }
        >
          <Plus className="size-10" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          {bitrix ? null : (
            <>
              <DropdownMenuItem
                className="h-12 px-4 text-base"
                onClick={() => navigate("/deals/create")}
              >
                {translate("resources.deals.forcedCaseName")}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="h-12 px-4 text-base"
                onClick={() => {
                  setContactCreateOpen(true);
                }}
              >
                {translate("resources.contacts.forcedCaseName")}
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onClick={() => {
              setNoteCreateOpen(true);
            }}
          >
            {translate("resources.notes.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onClick={() => {
              setTaskCreateOpen(true);
            }}
          >
            {translate("resources.tasks.forcedCaseName")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
